/** Who is driving right now — the page `/live` shows when nobody is named.
 *
 * The signed-out header offers "Spectate", and that link has to arrive
 * somewhere useful. `GET /api/live` answers with exactly what this needs and
 * decides the membership itself: a stranger sees public streams, a signed-in
 * driver also sees their own, an admin sees everybody's. So there is no
 * filtering here — the list is what the service was willing to show.
 *
 * Streams are addressed by account id rather than by login, because a login
 * belongs to a provider and changes when somebody renames themselves there.
 * The row shows the display name; the link carries the id.
 *
 * A signed-in driver also sees their own stream's visibility here, with the
 * switch and the address. This is the page "Spectate" in the header lands
 * on, so it is where "how do I let my friend watch?" gets asked, and the
 * answer used to be three screens away on the profile.
 */

import { Link } from 'react-router-dom';

import { EmptyState, ErrorNote, LoadingPanel, Page, PulseDot } from '@/components/ui';
import { count, hz, plural, relativeTime } from '@/lib/format';

import { StreamVisibility, useLiveVisibility } from './StreamVisibility';
import { useLiveListing } from './useLiveStream';

export function StreamList() {
  const { streams, enabled, loading, error, reload } = useLiveListing();
  const visibility = useLiveVisibility();

  return (
    <Page max={1100} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h1 style={{ fontSize: 24, margin: 0 }}>Spectate</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-neutral-500)' }}>
          Live position and lap times, four frames a second, straight from a driver's logger. No
          account needed for a public stream.
        </p>
      </div>

      {visibility && enabled ? (
        <div className="card elev-sm" style={{ padding: '12px 16px' }}>
          <StreamVisibility
            visibility={visibility}
            address={`${window.location.host}/live/${visibility.userId}`}
          />
        </div>
      ) : null}

      {loading ? <LoadingPanel label="looking for streams" /> : null}

      {!loading && error ? (
        <ErrorNote error={error} title="could not read who is streaming" onRetry={reload} />
      ) : null}

      {!loading && !error && !enabled ? (
        <EmptyState title="live streaming is switched off">
          The service is not carrying live streams at the moment. Track surveys and sessions are
          unaffected.
        </EmptyState>
      ) : null}

      {!loading && !error && enabled && streams.length === 0 ? (
        <EmptyState title="nobody is driving right now">
          A stream appears here the moment a logger connects with a <code>live:write</code> token
          and the driver has made their stream public.
        </EmptyState>
      ) : null}

      {streams.length > 0 ? (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}
        >
          {streams.map((stream) => (
            <li key={stream.user_id}>
              <Link
                to={`/live/${encodeURIComponent(stream.user_id)}`}
                className="card elev-sm"
                style={{ textDecoration: 'none', color: 'var(--color-text)', height: '100%' }}
              >
                <span
                  className="card-kicker"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
                >
                  <PulseDot size={7} glow />
                  live
                </span>
                <span className="card-title truncate">
                  {stream.display_name || stream.user_id}
                </span>
                <span style={{ fontSize: 13, color: 'var(--color-neutral-400)' }}>
                  {stream.track_name || stream.official_id || 'circuit unknown'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
                  {stream.car || 'car unknown'}
                </span>
                <span className="card-meta tabular-nums" style={{ marginTop: 'auto' }}>
                  {hz(stream.hz)} · {plural(stream.spectators, 'watching', 'watching')} ·{' '}
                  {count(stream.frames)} frames · started {relativeTime(stream.started_at)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </Page>
  );
}

export default StreamList;
